import clsx from 'clsx'

import type { KeyDataColour, KeyDataItem, KeyDataProps } from './types'

const colourClasses: Record<KeyDataColour, string> = {
	purple: 'bg-brand-200',
	green: 'bg-green-100',
	red: 'bg-red-100',
	beige: 'bg-cream-50',
}

function KeyDataCard({ item }: { item: KeyDataItem }) {
	if (!item.value && !item.label && !item.text) return null

	const bgClass = colourClasses[item.colour] ?? colourClasses.purple

	return (
		<div className={`${bgClass} h-75 p-6 rounded-xs flex flex-col justify-between`}>
			{item.value && <p className="display-big text-pure-black">{item.value}</p>}
			<div className="flex flex-col gap-2">
				{item.label && <p className="body-xl text-pure-black wrap-anywhere">{item.label}</p>}
				{item.text && <p className="body-xl font-semibold text-pure-black wrap-anywhere">{item.text}</p>}
			</div>
		</div>
	)
}

export default function KeyData({ items = [], overline, title, intro, isPart = false }: KeyDataProps) {
	const overlineText = overline && <p className="body text-black-950">{overline}</p>
	const titleText = title && <h2 className="h2 text-pure-black">{title}</h2>
	const introText = intro && (
		<p className={clsx('body text-black-950 max-w-200', (overlineText || titleText) && 'mt-6')}>{intro}</p>
	)
	const hasHeading = Boolean(overlineText || titleText || introText)

	// Cards are optional — an empty row renders nothing, so a part with no renderable
	// card must drop the grid entirely rather than leave the heading's gap trailing.
	const hasCards = items.some((item) => item.value || item.label || item.text)

	const gridClass =
		items.length > 3
			? 'grid grid-cols-1 gap-8 @min-[34.5rem]:grid-cols-2 @min-[72rem]:grid-cols-4'
			: 'grid grid-cols-1 gap-8 @min-[56rem]:grid-cols-3'

	const grid = hasCards && (
		<div className="@container keydata">
			<div className={gridClass}>
				{items.map((item, i) => (
					<KeyDataCard key={i} item={item} />
				))}
			</div>
		</div>
	)

	if (!hasHeading && !hasCards) return null

	const content = hasHeading ? (
		<div className="keydata flex flex-col gap-8">
			<div className="flex flex-col">
				{overlineText}
				{titleText}
				{introText}
			</div>
			{grid}
		</div>
	) : (
		grid
	)

	if (isPart) {
		return <div className="container my-16">{content}</div>
	}

	return <div className="my-12">{content}</div>
}
